import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  DecipherAccessTokenResponse,
  ServiceType,
} from '../../../../typings/api/skymap/rest/v0/access-token';
import { TokenService } from '../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { assertNever } from '../../utils/variables';
import { AccessTokenRedirect } from '../components/access-token-redirect/access-token-redirect';
import { useAuthState } from '../state/auth-state';
import { useUserCompaniesState } from '../state/user-companies-state';
import { useOptionalUserState } from '../state/user-state/user-state';
import { setAccessTokenRedirectFlag } from '../utils/access-token-redirect-flag';

type RedirectState = 'preparing' | 'authenticating' | 'redirecting' | 'failed';

const AccessTokenRedirectRoute = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginInMemory } = useAuthState();
  const { setActiveRole } = useOptionalUserState() ?? { setActiveRole: () => undefined };
  const { fetchUserCompanies } = useUserCompaniesState();
  const [redirectState, setRedirectState] = React.useState<RedirectState>('preparing');
  const hasRedirectedRef = React.useRef(false);

  const redirectToService = React.useCallback(
    async (service: ServiceType, resources: { name: string; value: string }[]) => {
      // Convert resources array to params object
      const params = resources.reduce((prev: Record<string, string>, cur) => {
        prev[cur.name] = cur.value;
        return prev;
      }, {});

      const lang = searchParams.get('lang');
      const langSuffix = lang ? `?lang=${encodeURIComponent(lang)}` : '';

      // Build React Router path based on service type
      let path: string;
      let projectId: string;

      switch (service) {
        case 'viewer': {
          projectId = params.projectId;
          if (!projectId) {
            throw new Error('Project ID is required for viewer service');
          }
          path = `/projects/${projectId}/viewer`;
          break;
        }
        case 'gcp-editor': {
          const geodataId = params.geodataId;
          if (!geodataId) {
            throw new Error('Geodata ID is required for gcp-editor service');
          }
          // Fetch geodata to get the project ID
          const geodata = await SkyMapAxiosServiceFactory.instance
            .createGeodataServiceV0()
            .fetchGeodataById({
              path: { geodataId },
              query: {},
            });
          projectId = geodata.data.projectId;
          if (!projectId) {
            throw new Error('Project ID not found in geodata');
          }
          path = `/projects/${projectId}/geodata/${geodataId}/viewer`;
          break;
        }
        case 'geodata-upload': {
          projectId = params.projectId;
          if (!projectId) {
            throw new Error('Project ID is required for geodata-upload service');
          }
          path = `/projects/${projectId}/geodata/upload`;
          break;
        }
        default:
          assertNever(service);
      }

      // Set module-scoped flag so fullscreen mode survives internal navigations
      // that lose location.state. The flag is isolated per JS context (iframe vs parent).
      setAccessTokenRedirectFlag();

      // Pass flag in location state to indicate this is from access token redirect
      // This will hide the AppLayout header when navigating
      void navigate(`${path}${langSuffix}`, {
        replace: true,
        state: { fromAccessTokenRedirect: true },
      });
    },
    [navigate, searchParams],
  );

  React.useEffect(() => {
    if (!token || hasRedirectedRef.current) {
      return;
    }

    hasRedirectedRef.current = true;

    const handleRedirect = async () => {
      try {
        setRedirectState('authenticating');

        const tokenService = new TokenService();
        const normalizedToken = token.replace(/[+]/g, '.');
        const extractedToken = tokenService.decodeToken(normalizedToken) as {
          userId: string;
          resource?: { name: string; value: string }[];
          service: ServiceType;
        };

        // Decipher the access token. The resulting auth token lives in module
        // scope only and is never written to localStorage. A pre-existing
        // token in localStorage from a prior sign-in is intentionally left
        // intact — the in-memory token wins on reads, so the iframe session
        // does not fall back to it.
        const accessTokenService = SkyMapAxiosServiceFactory.instance.createAccessTokenServiceV0();
        const decipherResponse = await accessTokenService.decipherAccessToken({ path: { token } });
        const accessTokenPayload: DecipherAccessTokenResponse['data'] = decipherResponse.data;

        if (!accessTokenPayload.authToken) {
          throw new Error('No auth token returned from decipher');
        }

        // Validate userId matches
        const authTokenPayload = tokenService.decodeToken(accessTokenPayload.authToken) as {
          userId: string;
        };
        if (authTokenPayload.userId !== extractedToken.userId) {
          throw new Error('Decyphered userId does not match access token user id');
        }

        // Authenticate with the in-memory token. loginInMemory installs the
        // token in module scope — deferring the write until after the userId
        // guard prevents a mismatched token from leaking into the iframe
        // session.
        await loginInMemory(accessTokenPayload.authToken);
        setActiveRole(null);

        // Fetch user and companies (user state may not be populated yet, so fetch directly)
        const response = await SkyMapAxiosServiceFactory.instance
          .createUserServiceV0()
          .fetchRequestingUser({});
        const user = response.data;
        if (!user) {
          throw new Error('Failed to fetch user');
        }
        await fetchUserCompanies(user.id, user.isOrganizationAdmin ?? false);

        setRedirectState('redirecting');

        // Navigate to the service
        await redirectToService(accessTokenPayload.service, accessTokenPayload.resource || []);
      } catch (err) {
        setRedirectState('failed');
        // eslint-disable-next-line no-console
        console.error('Access token redirect failed:', err);
      }
    };

    void handleRedirect();
  }, [token, loginInMemory, redirectToService, fetchUserCompanies, setActiveRole]);

  return <AccessTokenRedirect state={redirectState} />;
};

export { AccessTokenRedirectRoute };
